import { useState } from 'react'
import { Bell, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { useNotifications } from './NotificationProvider'
import { confClass, DetectionChips, AudioChips, CameraInfo, type DetectionNotification } from './shared'
import { useEventTitle } from '@/hooks/useEventTitle'  // #294

// ── Single drawer item ──────────────────────────────────────────────────────

function DrawerItem({ notif, onDismiss }: {
  notif: DetectionNotification
  onDismiss: (id: string) => void
}) {
  const { t } = useTranslation('common')
  const { t: tn } = useTranslation('notifications')
  const pct = notif.confidence !== undefined ? Math.round(notif.confidence * 100) : null
  // #294 — a critical event: the critical title, as on its toast
  const eventTitle = useEventTitle()
  const title = eventTitle({ ...notif.event, event_type: notif.eventType }, notif.title)

  // Relative time
  const elapsed = Date.now() - notif.createdAt
  let ago = t('time.justNow')
  if (elapsed > 60_000 * 60) ago = t('time.hoursAgo', { count: Math.floor(elapsed / 3_600_000) })
  else if (elapsed > 60_000) ago = t('time.minutesAgo', { count: Math.floor(elapsed / 60_000) })
  else if (elapsed > 10_000) ago = t('time.secondsAgo', { count: Math.floor(elapsed / 1_000) })

  return (
    <div className={cn('notif-drawer-item', !notif.isRead && 'notif-drawer-unread')}>
      {/* Unread dot */}
      {!notif.isRead && <span className="notif-drawer-unread-dot" />}

      {/* Thumbnail */}
      <div className="notif-drawer-thumb">
        {notif.thumbnailUrl ? (
          <img
            src={notif.thumbnailUrl}
            alt={title}
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
          />
        ) : (
          <div className="notif-drawer-thumb-placeholder">
            <Bell size={16} />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="notif-drawer-item-content">
        <div className="notif-drawer-item-top">
          <span className="notif-drawer-item-title">{title}</span>
          {pct !== null && (
            <span className={cn('det-conf-badge det-conf-badge-sm', confClass(notif.confidence!))}>{pct}%</span>
          )}
        </div>
        <CameraInfo camera={notif.camera} group={notif.cameraGroup} small />
        <DetectionChips objects={notif.detectedObjects} detectionMap={notif.detectionMap} />
        <AudioChips detections={notif.audioDetections} />
      </div>

      {/* Time */}
      <span className="notif-drawer-item-time">{notif.timestamp ?? ago}</span>

      {/* Dismiss */}
      <button
        className="notif-drawer-dismiss"
        onClick={() => onDismiss(notif.id)}
        aria-label={tn('peek.remove')}
      >
        <X size={14} />
      </button>
    </div>
  )
}

// ── Drawer ───────────────────────────────────────────────────────────────────

export function NotificationDrawer() {
  const { t } = useTranslation('notifications')
  const { t: tc } = useTranslation('common')
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false)
  const {
    notifications,
    drawerOpen,
    activeTab,
    toggleDrawer,
    markAllRead,
    clearAll,
    dismissFromDrawer,
    setActiveTab,
  } = useNotifications()

  const alertCount = notifications.filter(n => n.variant === 'alert').length
  const motionCount = notifications.filter(n => n.variant === 'standard').length
  const unreadCount = notifications.filter(n => !n.isRead).length

  const filtered = activeTab === 'all'
    ? notifications
    : activeTab === 'alerts'
      ? notifications.filter(n => n.variant === 'alert')
      : notifications.filter(n => n.variant === 'standard')

  const handleClearAll = () => {
    clearAll()
    setClearConfirmOpen(false)
  }

  return (
    <>
      {/* Overlay */}
      <div
        className={cn('notif-drawer-overlay', !drawerOpen && 'notif-drawer-hidden')}
        onClick={toggleDrawer}
      />

      {/* Panel */}
      <div className={cn('notif-drawer-panel', !drawerOpen && 'notif-drawer-hidden')}>

        {/* Header */}
        <div className="notif-drawer-header">
          <div>
            <div className="notif-drawer-title">{t('drawer.title')}</div>
            <div className="notif-drawer-subtitle">
              {t('drawer.newEvent', { count: unreadCount })}
            </div>
          </div>
          <div className="notif-drawer-actions">
            <button className="notif-drawer-btn" onClick={markAllRead}>{t('drawer.markAllRead')}</button>
            <button className="notif-drawer-btn" onClick={() => setClearConfirmOpen(true)}>{t('drawer.clearAll')}</button>
            <button className="notif-drawer-close" onClick={toggleDrawer} aria-label={t('drawer.closeDrawer')}>
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Filter tabs */}
        <div className="notif-drawer-tabs">
          <button
            className={cn('notif-drawer-tab', activeTab === 'all' && 'notif-drawer-tab-active')}
            onClick={() => setActiveTab('all')}
          >
            {t('tabs.all')} <span className="notif-drawer-tab-count">{notifications.length}</span>
          </button>
          <button
            className={cn('notif-drawer-tab', activeTab === 'alerts' && 'notif-drawer-tab-active')}
            onClick={() => setActiveTab('alerts')}
          >
            {t('tabs.alerts')} <span className="notif-drawer-tab-count">{alertCount}</span>
          </button>
          <button
            className={cn('notif-drawer-tab', activeTab === 'motion' && 'notif-drawer-tab-active')}
            onClick={() => setActiveTab('motion')}
          >
            {t('tabs.motion')} <span className="notif-drawer-tab-count">{motionCount}</span>
          </button>
        </div>

        {/* List */}
        <div className="notif-drawer-list">
          {filtered.length === 0 ? (
            <div className="notif-drawer-empty">
              <Bell size={32} strokeWidth={1.2} />
              <p>{t('empty.title')}</p>
              <p className="notif-drawer-empty-sub">{t('empty.subtitle')}</p>
            </div>
          ) : (
            filtered.map(notif => (
              <DrawerItem key={notif.id} notif={notif} onDismiss={dismissFromDrawer} />
            ))
          )}
        </div>
      </div>

      {/* Clear All confirmation */}
      <ConfirmDialog
        open={clearConfirmOpen}
        onOpenChange={setClearConfirmOpen}
        title={t('drawer.clearAllTitle', { defaultValue: 'Clear all notifications?' })}
        description={t('drawer.clearAllDesc', { defaultValue: 'This will remove all notifications from your drawer. This action cannot be undone.' })}
        confirmLabel={tc('actions.clearAll', { defaultValue: 'Clear all' })}
        variant="destructive"
        onConfirm={handleClearAll}
      />
    </>
  )
}
